import { useEffect, useState } from 'react'
import type { Connector } from 'wagmi'

interface Props {
  connectors: readonly Connector[]
  onConnect: (connector: Connector) => void | Promise<void>
  onClose: () => void
  error: string | null
}

/**
 * Wallet picker.
 *
 * Two things it deliberately does, both learned on the Forge:
 *
 *   1. It DEDUPLICATES ON THE RESOLVED PROVIDER OBJECT, not on the connector
 *      name. The same wallet is reachable through both the targeted MetaMask
 *      connector and the generic injected one, and the generic connector is
 *      always labelled "Injected" regardless of what it points at — so name
 *      matching would show one wallet twice under two names.
 *
 *   2. It HIDES CONNECTORS THAT CANNOT RESOLVE A PROVIDER. An absent MetaMask
 *      would otherwise render as a button that fails on click.
 */
export function ConnectDialog({ connectors, onConnect, onClose, error }: Props) {
  const [available, setAvailable] = useState<Connector[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const seen = new Set<unknown>()
      const usable: Connector[] = []
      for (const connector of connectors) {
        try {
          const provider = await connector.getProvider()
          if (!provider || seen.has(provider)) continue
          seen.add(provider)
          usable.push(connector)
        } catch {
          // No provider behind this entry — omit it rather than offer a dead button.
        }
      }
      if (!cancelled) setAvailable(usable)
    })()
    return () => {
      cancelled = true
    }
  }, [connectors])

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Connect a wallet" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Connect a wallet</h2>
        {available.length === 0 ? (
          <p className="hint">No browser wallet detected. Install one, then reload this page.</p>
        ) : (
          <ul className="wallets">
            {available.map((connector) => (
              <li key={connector.uid}>
                <button className="wallet" onClick={() => onConnect(connector)}>
                  {connector.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <p className="alert small">{error}</p>}
        <button className="ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  )
}
